<template>
    <div>
        <Sidebar :analytic_id="analytic_id" :rowfill="analytic.rowfill" v-if="analytic"/>

        <div class="toolbar" v-if="images">
            <!-- Global Controls Section -->
            <div class="toolbar-controls">
                <button class="medium-icon-button" @click="saveChanges" :disabled="changes.length == 0 || isRefreshing" title="Save Changes">
                    <i class="fa fa-save"></i>
                </button>
                <button class="medium-icon-button" @click="discardChanges" :disabled="changes.length == 0 || isRefreshing" title="Discard Changes">
                    <i class="fa fa-undo"></i>
                </button>
                <button class="medium-icon-button" @click="refreshPage" :disabled="isRefreshing || loadingThumbnails" title="Refresh">
                    <i :class="['fa', 'fa-refresh', { 'spin': isRefreshing || loadingThumbnails}]"></i>
                </button>
            </div>
            <hr class="divider">

            <!-- Thumbnails Section -->
            <div class="toolbar-section">
                <span class="section-label">Thumbnails</span>
                <div class="icon-row">
                    <button class="small-icon-button" @click="toggleThumbnails" :disabled="isRefreshing || loadingThumbnails" title="Show/Hide Thumbnails">
                        <i :class="showAllThumbnails ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
                    </button>
                    <button class="small-icon-button" @click="decreaseThumbnailSize" title="Decrease Thumbnail Size">
                        <i class="fa fa-minus"></i>
                    </button>
                    <button class="small-icon-button" @click="increaseThumbnailSize" title="Increase Thumbnail Size">
                        <i class="fa fa-plus"></i>
                    </button>
                </div>
            </div>
            <hr class="divider">

            <div class="toolbar-section">
                <span class="total-count">Total: {{ images.length }} Image(s)</span> <!-- Total image count label -->
            </div>

            <!-- Rejected Section -->
            <div class="toolbar-section">
                <div class="label-icon-row">
                    <div class="eye-icon-container">
                        <button class="small-icon-button" @click="toggleRejectedImages" title="Show/Hide Rejected">
                            <i :class="showRejectedImages ?  'fa fa-eye' : 'fa fa-eye-slash'"></i>
                        </button>
                    </div>
                    <div class="label-and-count">
                        <span class="section-label">Rejected</span>
                        <span class="image-count">{{ rejectedCount }} Image(s)</span>
                    </div>
                </div>
            </div>

            <!-- Completed Section -->
            <div class="toolbar-section">
                <div class="label-icon-row">
                    <div class="eye-icon-container">
                        <button class="small-icon-button" @click="toggleCompletedImages" title="Show/Hide Completed">
                            <i :class="showCompletedImages ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
                        </button>
                    </div>
                    <div class="label-and-count">
                        <span class="section-label">Completed</span>
                        <span class="image-count">{{ completedCount }} Image(s)</span>
                    </div>
                </div>
            </div>

            <!-- Unfinished Section -->
            <div class="toolbar-section">
                <div class="label-icon-row">
                    <div class="eye-icon-container">
                        <button class="small-icon-button" @click="toggleUnfinishedImages" title="Show/Hide Unfinished">
                            <i :class="showUnfinishedImages ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
                        </button>
                    </div>
                    <div class="label-and-count">
                        <span class="section-label">Unfinished</span>
                        <span class="image-count">{{ unfinishedCount }} Image(s)</span>
                    </div>
                </div>
            </div>
            <hr class="divider">

            <!-- Bulk QA Section -->
            <div class="toolbar-section">
                <div class="bulk-qa-header">
                    <span class="section-label">Bulk QA</span>
                    <button 
                        class="small-icon-button bulk-qa-apply-button" 
                        @click="applyBulkQA" 
                        :disabled="isRefreshing"
                        title="Bulk Apply QA Status Change"
                    >
                        <i class="fa fa-play"></i>
                    </button>
                </div>
                
                <!-- QA Type and Status Selection in Two Columns -->
                <div class="bulk-qa-options">
                    <div class="bulk-qa-columns">
                        <!-- QA Type Column -->
                        <div class="bulk-qa-column">
                            <div class="radio-group">
                                <span class="radio-group-label">QA Type:</span>
                                <label class="radio-option">
                                    <input type="radio" v-model="bulkQAType" value="row_qa_status" />
                                    <span>Row</span>
                                </label>
                                <label class="radio-option">
                                    <input type="radio" v-model="bulkQAType" value="ppa_qa_status" :disabled="analytic.rowfill" />
                                    <span :class="{'disabled': analytic.rowfill}">PPA</span>
                                </label>
                                <label class="radio-option">
                                    <input type="radio" v-model="bulkQAType" value="gender_qa_status" />
                                    <span>Gender</span>
                                </label>
                            </div>
                        </div>
                        
                        <!-- Vertical Divider -->
                        <div class="bulk-qa-divider"></div>
                        
                        <!-- Status Column -->
                        <div class="bulk-qa-column">
                            <div class="radio-group">
                                <span class="radio-group-label">Status:</span>
                                <label class="radio-option">
                                    <input type="radio" v-model="bulkQAStatus" value="completed" />
                                    <span>Approve</span>
                                </label>
                                <label class="radio-option">
                                    <input type="radio" v-model="bulkQAStatus" value="rejected" />
                                    <span>Reject</span>
                                </label>
                                <label class="radio-option">
                                    <input type="radio" v-model="bulkQAStatus" value="not_done" />
                                    <span>Reset</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="grid-page">
            <h1 class="progress-title">Progress</h1>

            <!-- Thumbnail grid -->
            <div v-if="images" class="thumbnail-grid">
                <div 
                    v-for="(image, index) in images" 
                    :key="index" 
                    class="thumbnail-item" 
                    @mouseover="hoveredImage = image" 
                    @mouseleave="hoveredImage = null"
                    v-if="image.isVisible"
                >
                    <div 
                        class="status-bar-container" 
                        :style="{ width: (thumbnailSize + 100) + 'px' }" 
                        :class="[getImageStatusClass(image), { 'disabled': isRefreshing }]"
                    >
                        <!-- Image Name with Toggle Thumbnail Button -->
                        <div class="image-name-toggle">
                            <div class="image-name">{{ image.name }}</div>
                            <button class="toggle-thumbnail" @click="toggleImageThumbnail(image)" :disabled="loadingThumbnails">
                                <span :class="image.showThumbnail ? 'arrow-down' : 'arrow-left'"></span>
                            </button>
                        </div>

                        <!-- Thumbnail container with RGB/Binary, Row, and Keypoints overlays -->
                        <div v-if="image.showThumbnail" class="thumbnail-container">
                            <!-- Switch between RGB and Binary Thumbnail -->
                            <img :src="image.showBinary ? image.binary_thumbnail_url : image.rgb_thumbnail_url" alt="Thumbnail" class="thumbnail-img" :style="{ width: thumbnailSize + 'px', height: 'auto' }"/>
                            <!-- Row Thumbnail (overlay) -->
                            <img v-if="image.showRows" :src="image.rows_thumbnail_url" alt="Row Thumbnail" class="row-thumbnail" :style="{ width: thumbnailSize + 'px', height: 'auto' }"/>
                            <!-- Keypoints Thumbnail (overlay) -->
                            <img v-if="image.showKeypoints" :src="image.keypoints_thumbnail_url" alt="Keypoints Thumbnail" class="keypoints-thumbnail" :style="{ width: thumbnailSize + 'px', height: 'auto' }"/>

                            <!-- 3x3 Grid Overlay for Subimages (Always on, buffered) -->
                            <div class="grid-overlay" :style="getBufferedStyle()">
                                <div v-for="(subimage, subIndex) in image.subimages" 
                                    :key="subIndex" 
                                    class="grid-cell"
                                    :class="{ 'changed-cell': isSubimageChanged(image, subIndex) }" 
                                    @click="toggleSubimageStatus(image, subIndex)"
                                >
                                    <!-- Display scalable 'X' if subimage is rejected -->
                                    <div v-if="subimage.rejected" class="grid-rejected"></div>
                                </div>
                            </div>
                        </div>

                        <!-- Status Bar -->
                        <div class="status-bar">
                            <!-- Lock Information: Lock Icon and Locked By Name -->
                            <div class="lock-info">
                                <button 
                                    @click="toggleLock(image)" 
                                    class="qa-status-button" 
                                    :class="[{ 'changed-qa': isLockChanged(image), 'locked': image.locked_by && !isLockChanged(image) }]"
                                    @mouseover="showLockTooltip(image)" 
                                    @mouseleave="hideLockTooltip(image)"
                                >
                                    {{ image.locked_by ? '\u{1F512}' : '\u{1F513}' }} <!-- Locked/Unlocked Icon -->
                                </button>
                                <span class="lock-status">{{ getLockedByName(image.locked_by) }}</span>

                                <!-- Tooltip to display user list -->
                                <div v-if="image.lockHovered" class="user-list-tooltip">
                                    {{ lockTooltipValue(image) }}
                                </div>
                            </div>

                            <!-- Row QA Status -->
                            <div class="qa-status-item" :class="getQALabelClass(image, 'row_qa_status')">
                                <span class="qa-status-label" @click.prevent="openQAInNewTab(image, RouteNames.RowQA)">Row QA</span>
                                <button 
                                    @click="toggleQAStatus(image, 'row_qa_status')" 
                                    class="qa-status-button"
                                    :class="{ 'changed-qa': isQAChanged(image, 'row_qa_status') }"
                                >
                                    {{ getQAStatusIcon(image.row_qa_status) }}
                                </button>
                            </div>

                            <!-- PPA QA Status -->
                            <div class="qa-status-item" :class="getQALabelClass(image, 'ppa_qa_status')">
                                <span 
                                    class="qa-status-label" 
                                    @click.prevent="!analytic.rowfill && openQAInNewTab(image, RouteNames.PlantCountQA)" 
                                    :class="{'disabled': analytic.rowfill}"
                                >
                                    PPA QA
                                </span>
                                <button 
                                    @click="toggleQAStatus(image, 'ppa_qa_status')" 
                                    class="qa-status-button" 
                                    :class="{ 'changed-qa': isQAChanged(image, 'ppa_qa_status') }"
                                >
                                    {{ getQAStatusIcon(image.ppa_qa_status) }}
                                </button>
                            </div>

                            <!-- Gender QA Status -->
                            <div class="qa-status-item" :class="getQALabelClass(image, 'gender_qa_status')">
                                <span class="qa-status-label" @click.prevent="openQAInNewTab(image, RouteNames.GenderQA)">Gender QA</span>
                                <button 
                                    @click="toggleQAStatus(image, 'gender_qa_status')" 
                                    class="qa-status-button"
                                    :class="{ 'changed-qa': isQAChanged(image, 'gender_qa_status') }"
                                >
                                    {{ getQAStatusIcon(image.gender_qa_status) }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else>
                Loading...
            </div>
        </div>
    </div>
</template>

<script>
import { getAnalytic, getImages, patchSubimage, updateQAStatus, lockImage, unlockImage, getImage } from '../utils/api_endpoints.js';
import { get_key_from_route_params, navigateToRoute } from "../utils/nav_utils.js"; // Restored the import
import { get_username } from '../utils/qa_utils.js';
import Sidebar from './Sidebar.vue'; // Import the Sidebar component
import { RouteNames } from '../utils/constants.js';

export default {
    components: {
        Sidebar // Register Sidebar component here
    },
    computed: {
        rejectedCount() {
            return this.images.filter(image => {
                const status = this.getImageStatus(image);
                return status.isRejected;
            }).length;
        },
        completedCount() {
            return this.images.filter(image => {
                const status = this.getImageStatus(image);
                return status.isCompleted;
            }).length;
        },
        unfinishedCount() {
            return this.images.filter(image => {
                const status = this.getImageStatus(image);
                return status.isUnfinished;
            }).length;
        }
    },
    data() {
        return {
            analytic: null,
            images: null,
            thumbnailSize: 700, // Default thumbnail size in pixels
            showAllThumbnails: false,
            showAllBinaries: false, 
            showAllRows: false,
            showAllKeypoints: false,
            isRefreshing: false,
            showRejectedImages: true, // Track visibility of rejected images
            showCompletedImages: true, // Track visibility of completed images
            showUnfinishedImages: true, // Track visibility of unfinished images
            loadingThumbnails: false,
            hoveredImage: null, // Track hovered image
            changes: [], // Centralized list of changes
            originalImages: {}, // Store original image states
            RouteNames: RouteNames,
            // Bulk QA properties
            bulkQAType: 'row_qa_status', // Default QA type
            bulkQAStatus: 'completed', // Default status
        }
    },
    async created() {
        this.analytic_id = get_key_from_route_params(this, "analytic_id");
        let images = await getImages(this.analytic_id);
        this.analytic = await getAnalytic(this.analytic_id);

        // Initialize overlay visibility for each image
        images.forEach(image => {
            this.originalImages[image.id] = JSON.parse(JSON.stringify(image));

            this.$set(image, 'showThumbnail', false); 
            this.$set(image, 'showBinary', false); 
            this.$set(image, 'showRows', false);
            this.$set(image, 'showKeypoints', false); 
            this.$set(image, 'isVisible', true); 
            this.$set(image, 'lockHovered', false)
        });

        // Delay displaying image status bars until everything is loaded
        this.images = images;

        // Add event listeners for keybindings
        window.addEventListener('keydown', this.handleKeydown);
    },
    methods: {
        handleKeydown(event) {
            if (event.key === 'R') {
                this.toggleAllRowOverlays();
            } else if (event.key === 'r' && this.hoveredImage !== null) {
                this.toggleRowOverlay(this.hoveredImage);
            } else if (event.key === 'K') {
                this.toggleAllKeypointOverlays();
            } else if (event.key === 'k' && this.hoveredImage !== null) {
                this.toggleKeypointOverlay(this.hoveredImage);
            } else if (event.key === 'B') {
                this.toggleAllBinaryThumbnails();
            } else if (event.key === 'b' && this.hoveredImage !== null) {
                this.toggleBinaryThumbnail(this.hoveredImage);
            } else if (event.key === '-') {
                this.decreaseThumbnailSize()
            } else if (event.key === '=') {
                this.increaseThumbnailSize()
            } else if (event.key === 's' && event.ctrlKey) {
                event.preventDefault();
                this.saveChanges();
            } else if (event.key === 't' && this.hoveredImage !== null) {
                this.toggleImageThumbnail(this.hoveredImage);
            } else if (event.key === 'T') {
                this.toggleThumbnails();
            }
        },
        toggleAllRowOverlays() {
            this.showAllRows = !this.showAllRows;
            this.images.forEach(image => {
                this.$set(image, 'showRows', this.showAllRows);
            });
        },
        toggleRowOverlay(image) {
            this.$set(image, 'showRows', !image.showRows);
        },
        toggleAllKeypointOverlays() {
            this.showAllKeypoints = !this.showAllKeypoints;
            this.images.forEach(image => {
                this.$set(image, 'showKeypoints', this.showAllKeypoints);
            });
        },
        toggleKeypointOverlay(image) {
            this.$set(image, 'showKeypoints', !image.showKeypoints);
        },
        toggleAllBinaryThumbnails() {
            this.showAllBinaries = !this.showAllBinaries;
            this.images.forEach(image => {
                this.$set(image, 'showBinary', this.showAllBinaries);
            });
        },
        toggleBinaryThumbnail(image) {
            this.$set(image, 'showBinary', !image.showBinary);
        },
        async toggleThumbnails() {
            this.showAllThumbnails = !this.showAllThumbnails;

            this.loadingThumbnails = true;
            
            try {
                const thumbnailPromises = this.images.map(image => this.toggleImageThumbnail(image, this.showAllThumbnails));
                await Promise.all(thumbnailPromises);
            } catch (error) {
                console.error('Error loading thumbnails:', error);
            } finally {
                this.loadingThumbnails = false;
            }
        },
        async toggleImageThumbnail(image, showThumbnail = null) {
            if (showThumbnail === null) {
                showThumbnail = !image.showThumbnail;
            }

            if (showThumbnail && !image.rgb_thumbnail_url) {
                try {
                    const updatedImage = await getImage(image.id, true);
                    this.$set(image, 'rgb_thumbnail_url', updatedImage.rgb_thumbnail_url);
                    this.$set(image, 'binary_thumbnail_url', updatedImage.binary_thumbnail_url);
                    this.$set(image, 'rows_thumbnail_url', updatedImage.rows_thumbnail_url);
                    this.$set(image, 'keypoints_thumbnail_url', updatedImage.keypoints_thumbnail_url);
                } catch (error) {
                    console.error(`Error loading thumbnail for image ${image.id}:`, error);
                    // Don't prevent setting showThumbnail even if thumbnail loading fails
                }
            }

            this.$set(image, 'showThumbnail', showThumbnail);
        },
        increaseThumbnailSize() {
            if (this.thumbnailSize < window.innerWidth - 650) {
                this.thumbnailSize += 20;
            }
        },
        decreaseThumbnailSize() {
            if (this.thumbnailSize > 200) {
                this.thumbnailSize -= 20;
            }
        },
        trackChange(imageId, field, newValue, subimageIdx = null) {
            const originalValue = subimageIdx !== null 
                ? this.originalImages[imageId].subimages[subimageIdx][field]
                : this.originalImages[imageId][field];

            if (newValue !== originalValue) {
                const existingChange = this.changes.find(change => 
                    change.imageId === imageId && change.field === field && change.subimageIdx === subimageIdx);

                if (existingChange) {
                    existingChange.newValue = newValue;
                } else {
                    this.changes.push({ imageId, field, newValue, subimageIdx });
                }
            } else {
                // Remove the change if it reverts to the original state
                this.changes = this.changes.filter(change => 
                    !(change.imageId === imageId && change.field === field && change.subimageIdx === subimageIdx));
            }
        },
        toggleQAStatus(image, qaField) {
            // Cycle through 'not_done', 'completed', and 'rejected'
            if (image[qaField] === 'not_done') {
                image[qaField] = 'completed';
            } else if (image[qaField] === 'completed') {
                image[qaField] = 'rejected';
            } else {
                image[qaField] = 'not_done';
            }

            this.trackChange(image.id, qaField, image[qaField]);
        },
        toggleLock(image) {
            if (image.locked_by === null) {
                image.locked_by = get_username();
            } else {
                image.locked_by = null;
            }
            
            this.trackChange(image.id, 'locked_by', image.locked_by);
        },
        toggleSubimageStatus(image, subimageIndex) {
            const subimage = image.subimages[subimageIndex];
            const newStatus = !subimage.rejected;
            
            subimage.rejected = newStatus;
            this.trackChange(image.id, 'rejected', newStatus, subimageIndex);
        },
        toggleRejectedImages() {
            this.showRejectedImages = !this.showRejectedImages;
            this.refreshImageVisibility();
        },
        toggleCompletedImages() {
            this.showCompletedImages = !this.showCompletedImages;
            this.refreshImageVisibility();
        },
        toggleUnfinishedImages() {
            this.showUnfinishedImages = !this.showUnfinishedImages;
            this.refreshImageVisibility();
        },
        getBufferedStyle() {
            let buffer_pct = this.analytic?.image_buffer_pct || 0;
            let remaining_pct = 100 - (buffer_pct * 2);
            const gridWidth = this.analytic?.subimage_grid_width || 3;
            const gridHeight = this.analytic?.subimage_grid_height || 3;
            return {     
                display: 'grid',
                gridTemplateColumns: `repeat(${gridWidth}, 1fr)`,
                gridTemplateRows: `repeat(${gridHeight}, 1fr)`,
                position: 'absolute',
                top: buffer_pct + "%",
                left: buffer_pct + "%",
                height: remaining_pct + "%",
                width: remaining_pct + "%",
                boxSizing: 'border-box',
                border: '2px solid rgba(255, 255, 255, 0.8)',
            }
        },
        async saveChanges() {
            this.isRefreshing = true;

            try {
                // Process all changes concurrently using Promise.all
                const changePromises = this.changes.map(async (change) => {
                    try {
                        if (change.field === 'locked_by') {
                            // Handle locking and unlocking separately
                            if (change.newValue) {
                                await lockImage(change.imageId); // Lock the image
                            } else {
                                await unlockImage(change.imageId); // Unlock the image
                            }
                        } else if (change.subimageIdx !== null) {
                            await patchSubimage(change.imageId, change.subimageIdx, change.newValue)
                        } else {
                            // Handle QA status and subimage status changes using updateQAStatus
                            await updateQAStatus(change.imageId, change.field, change.newValue);
                        }
                        return { success: true, change };
                    } catch (error) {
                        console.error(`Error applying change for image ${change.imageId}:`, error);
                        return { success: false, change, error };
                    }
                });

                // Wait for all changes to complete
                const results = await Promise.all(changePromises);

                // Check for any failures
                const failedChanges = results.filter(result => !result.success);
                if (failedChanges.length > 0) {
                    console.error(`Failed to apply ${failedChanges.length} changes:`, failedChanges);
                    alert(`Warning: ${failedChanges.length} changes failed to save. Check console for details.`);
                }

                this.changes = [];
                this.images.forEach(image => {
                    this.originalImages[image.id] = JSON.parse(JSON.stringify(image));
                });
                
                // Trigger re-render
                this.$forceUpdate();
                
                // Give users a moment to see what was saved, then apply visibility rules
                setTimeout(() => {
                    this.refreshImageVisibility();
                }, 500); // 0.5 second delay for verification
            } catch (error) {
                console.error('Error during save changes:', error);
                alert('An unexpected error occurred while saving changes. Please try again.');
            } finally {
                this.isRefreshing = false;
            }
        },
        isLockChanged(image) {
            return image.locked_by !== this.originalImages[image.id].locked_by;
        },
        isQAChanged(image, qaField) {
            return image[qaField] !== this.originalImages[image.id][qaField];
        },
        isSubimageChanged(image, subimageIndex) {
            const originalStatus = this.originalImages[image.id].subimages[subimageIndex].rejected;
            return image.subimages[subimageIndex].rejected !== originalStatus;
        },
        getQAStatusIcon(status) {
            if (status === 'not_done') return '\u2753'; // Unicode Question Mark
            if (status === 'completed') return '\u2705'; // Unicode Green Check Mark
            if (status === 'rejected') return '\u274C'; // Unicode Red X
            return ''; // Default to no icon if status is invalid
        },
        getImageStatus(image) {
            const originalImage = this.originalImages[image.id];

            // Compare only QA status fields
            const hasUnsavedChanges = (
                image.ppa_qa_status !== originalImage.ppa_qa_status ||
                image.row_qa_status !== originalImage.row_qa_status ||
                image.gender_qa_status !== originalImage.gender_qa_status
            );

            // If no unsaved QA changes, determine status based on current values
            const { ppa_qa_status, row_qa_status, gender_qa_status } = image;
            
            const isRejected = ppa_qa_status === 'rejected' || row_qa_status === 'rejected' || gender_qa_status === 'rejected';
            const isCompleted = (ppa_qa_status === 'completed' || this.analytic.rowfill) && 
                              row_qa_status === 'completed' && 
                              gender_qa_status === 'completed';
            const isUnfinished = !isCompleted && !isRejected;

            return { hasUnsavedChanges, isRejected, isCompleted, isUnfinished };
        },
        getImageStatusClass(image) {
            const status = this.getImageStatus(image);

            if (status.hasUnsavedChanges) {
                return ''; // No status class if there are unsaved QA changes
            }

            if (status.isRejected) {
                return 'rejected-status-bar'; // Apply red background for rejected status
            }
            
            if (status.isCompleted) {
                return 'completed-status-bar'; // Apply green background for completed status
            }

            return ''; // No specific status class
        },
        getLockedByName(email) {
            if (!email) return '';
            return email.split('@')[0]; // Return the part before '@'
        },
        openQAInNewTab(image, route_name) {
            navigateToRoute(this, route_name, this.analytic_id, {image_id: image.id, "new_tab": true}, true)
        },
        showLockTooltip(image) {
            image.lockHovered = true;
        },
        hideLockTooltip(image) {
            image.lockHovered = false;
        },
        lockTooltipValue(image) {
            if (image.locked_at && image.locked_by) {
                return new Date(image.locked_at).toLocaleString(); 
            } else {
                return image.users.map(email => this.getLockedByName(email)).join(", ");
            }
        },
        async refreshImage(image) {
            try {
                // Fetch the updated image data
                const updatedImage = await getImage(image.id, image.showThumbnail);

                // Check if we got valid data
                if (!updatedImage) {
                    console.error(`Failed to refresh image ${image.id}: No data returned`);
                    return;
                }

                for (const key in updatedImage) {
                    if (Object.prototype.hasOwnProperty.call(updatedImage, key)) {
                        this.$set(image, key, updatedImage[key]);
                    }
                }

                // Only update subimages if they exist
                if (updatedImage.subimages && Array.isArray(updatedImage.subimages)) {
                    updatedImage.subimages.forEach((updatedSubimage, index) => {
                        if (image.subimages && image.subimages[index]) {
                            this.$set(image.subimages[index], 'rejected', updatedSubimage.rejected);
                        }
                    });
                }

                // Update the original image state to reflect the new values (including subimages)
                this.originalImages[image.id] = JSON.parse(JSON.stringify(updatedImage));
            } catch (error) {
                console.error(`Error refreshing image ${image.id}:`, error);
                // Don't throw the error - let other images continue to refresh
            }
        },
        async refreshPage() {
            // Check if there are unsaved changes
            if (this.changes.length > 0) {
                const confirmDiscard = confirm('You have unsaved changes. Do you want to discard them?');
                if (!confirmDiscard) {
                    // User doesn't want to discard changes, don't refresh
                    return;
                } else {
                    // Clear changes if user confirms
                    this.changes = [];
                }
            }

            // Set global refreshing state
            this.isRefreshing = true;

            try {
                // Refresh all images in parallel
                const refreshPromises = this.images.map(image => this.refreshImage(image));
                await Promise.all(refreshPromises);
            } catch (error) {
                console.error('Error during page refresh:', error);
            } finally {
                // Always turn off global refreshing state
                this.isRefreshing = false;
            }

            this.refreshImageVisibility()

            // Trigger re-render
            this.$forceUpdate();
        },
        getQALabelClass(image, qa_status) {
            if (image[qa_status] === 'completed' && !this.getImageStatusClass(image)) {
                return 'qa-approved';
            } else if (image[qa_status] === 'rejected' && !this.getImageStatusClass(image)) {
                return 'qa-rejected';
            } else {
                return '';
            }
        },
        discardChanges() {
            this.images.forEach((image) => {
                const original = this.originalImages[image.id];

                image.ppa_qa_status = original.ppa_qa_status;
                image.row_qa_status = original.row_qa_status;
                image.gender_qa_status = original.gender_qa_status;

                image.subimages.forEach((subimage, idx) => {
                    subimage.rejected = original.subimages[idx].rejected;
                });

                image.locked_by = original.locked_by;
            });

            // Clear the changes array
            this.changes = [];
        },
        refreshImageVisibility() {
            // Re-apply visibility rules for all images based on current toggle states
            this.images.forEach(image => {
                let shouldBeVisible = true;

                const status = this.getImageStatus(image);

                if (status.hasUnsavedChanges) {
                    // Always keep images with unsaved changes visible
                    shouldBeVisible = true;
                } else {
                    // For saved images, check status and toggle states
                    // Hide if the corresponding toggle is off
                    if (status.isRejected && !this.showRejectedImages) {
                        shouldBeVisible = false;
                    } else if (status.isCompleted && !this.showCompletedImages) {
                        shouldBeVisible = false;
                    } else if (status.isUnfinished && !this.showUnfinishedImages) {
                        shouldBeVisible = false;
                    }
                }

                this.$set(image, "isVisible", shouldBeVisible);
            });
        },
        async applyBulkQA() {
            // Check if there are unsaved changes
            if (this.changes.length > 0) {
                const confirmProceed = confirm('You have unsaved changes. Do you want to save them first?');
                if (confirmProceed) {
                    await this.saveChanges();
                } else {
                    return; // Don't proceed if user doesn't want to save
                }
            }

            // Filter images that need the selected QA type updated
            const unfinishedImages = this.images.filter(image => {
                const currentStatus = image[this.bulkQAType];
                
                // If selecting "Reset" (not_done), include any image that isn't already not_done
                if (this.bulkQAStatus === 'not_done') {
                    return currentStatus !== 'not_done';
                }
                
                // If selecting "Approve" (completed) or "Reject" (rejected), 
                // only include images that are currently not_done
                if (this.bulkQAStatus === 'completed' || this.bulkQAStatus === 'rejected') {
                    return currentStatus === 'not_done';
                }
                
                return false;
            });

            if (unfinishedImages.length === 0) {
                alert(`No images found that need ${this.bulkQAType.replace('_qa_status', '')} QA status changed to ${this.bulkQAStatus}.`);
                return;
            }

            const confirmMessage = `This will update ${this.bulkQAType.replace('_qa_status', '')} QA status to "${this.bulkQAStatus}" for ${unfinishedImages.length} image(s). Continue?`;
            if (!confirm(confirmMessage)) {
                return;
            }

            this.isRefreshing = true;

            try {
                let successCount = 0;
                let failedImages = [];

                // Process all images concurrently using Promise.all
                const updatePromises = unfinishedImages.map(async (image) => {
                    try {
                        // Call the existing single update API
                        await updateQAStatus(image.id, this.bulkQAType, this.bulkQAStatus);
                        return { success: true, image };
                        
                    } catch (error) {
                        console.error(`Error updating image ${image.id}:`, error);
                        return { success: false, image, error };
                    }
                });

                // Wait for all updates to complete
                const results = await Promise.all(updatePromises);

                // Process results
                results.forEach(result => {
                    if (result.success) {
                        successCount++;
                    } else {
                        failedImages.push(result.image.name || `Image ${result.image.id}`);
                    }
                });
                
                // Refresh all statuses
                await this.refreshPage();

                // Show results
                if (failedImages.length === 0) {
                    alert(`Successfully updated all ${successCount} image(s).`);
                } else {
                    alert(`Updated ${successCount} image(s) successfully.\n\nFailed to update ${failedImages.length} image(s):\n${failedImages.join(', ')}`);
                }
            } catch (error) {
                console.error('Error during bulk QA update:', error);
                alert('An unexpected error occurred during the bulk update. Please try again.');
            } finally {
                this.isRefreshing = false;
            }
        }
    },
    beforeDestroy() {
        // Remove the keybinding event listener
        window.removeEventListener('keydown', this.handleKeydown);
    }
}
</script>

<style scoped>
.grid-page {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 95px;
    margin-left: 220px;
    margin-right: 250px;
}

.progress-title {
    margin-bottom: 20px;
    font-weight: bold;
    color: #333;
}

.thumbnail-grid {
    display: flex;
    flex-direction: column;
    gap: 10px; /* Adjust spacing between thumbnails */
}

.thumbnail-img, .row-thumbnail, .keypoints-thumbnail {
    display: block;
    max-width: 100%; 
    height: auto; /* Preserve aspect ratio */
}

.row-thumbnail, .keypoints-thumbnail {
    position: absolute; /* Position the overlays on top of the RGB thumbnail */
    top: 0;
    left: 0;
    opacity: 0.8; /* Optional: You can adjust opacity if needed */
}

.grid-cell {
    position: relative;
    box-sizing: border-box;
    border: 2px solid rgba(255, 255, 255, 0.8); /* Optional: Grid cell borders */
}

.grid-rejected {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    background: transparent;
    display: block;
}

.grid-rejected:before, .grid-rejected:after {
    position: absolute;
    content: '';
    background: red;
    display: block;
    width: 80%;
    height: 15%;
    top: 50%;
    left: 50%;
    transform-origin: center center;
    transform: translate(-50%, -50%) rotate(-45deg);
}

.grid-rejected:after {
    transform: translate(-50%, -50%) rotate(45deg);
}

.resize-controls {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
}

.resize-controls button {
    margin: 0 10px;
    padding: 5px 10px;
    font-size: 16px;
    cursor: pointer;
}

.changed-cell {
    background-color: rgba(255, 255, 0, 0.5); /* Highlight unsaved changes */
}

/* Container for the status bar and image name */
.status-bar-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: #f9f9f9;
    padding: 10px;
    border-radius: 10px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    min-width: 800px;
    margin: 0 auto;
}

/* Disable status bar container when refreshing */
.status-bar-container.disabled {
    opacity: 0.6;
    pointer-events: none;
}

.image-name-toggle {
    display: flex;
    justify-content: center; /* Center align the content */
    width: 100%;
    align-items: center;
    position: relative;
}

.image-name {
    font-size: 20px;
    font-weight: 600;
    color: #333;
    text-align: center; /* Ensures the text is centered */
    flex-grow: 1; /* Ensures the name takes up available space */
}

.toggle-thumbnail {
    background: none;
    border: none;
    cursor: pointer;
    color: #007bff;
    position: relative;
    right: 0px;
}

.arrow-down, .arrow-left {
    display: inline-block;
    width: 0;
    height: 0;
    border-style: solid;
}

.arrow-down {
    border-width: 12px 6px 0 6px; /* Creates a downward-pointing arrow */
    border-color: #444444 transparent transparent transparent;
}

.arrow-left {
    border-width: 6px 12px 6px 0; /* Creates a leftward-pointing arrow */
    border-color: transparent #444444 transparent transparent;
}

/* Status Bar Grid Layout */
.status-bar {
    display: grid;
    grid-template-columns: repeat(4, 1fr); /* Four equal sections for lock and QA statuses */
    width: 100%;
    align-items: center;
    margin-top: 10px;
    padding: 10px;
}

/* Thumbnail Item */
.thumbnail-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 auto; /* Center the thumbnail items */
}

.thumbnail-container {
    display: flex;
    justify-content: center;
    background-color: #f9f9f9; /* Ensure it matches the status bar */
    position: relative; /* Ensure that the grid-overlay is positioned correctly relative to this container */
}

/* Lock Information Styling */
.lock-info {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

.lock-status {
    margin-left: 10px;
    font-weight: 500;
    max-width: 130px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis; 
}

/* QA Status Item Styling */
.qa-status-item {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px;
}

/* QA Status Button Styling */
.qa-status-button {
    padding: 6px 12px;
    border: 1px solid rgba(0, 0, 0, 0.2); /* No border by default */
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
    background: none;
    text-align: center;
}

/* Hover effect: subtle change to make it clear it's clickable */
.qa-status-button:hover {
    background-color: rgba(0, 0, 0, 0.1); /* Light darkening of the background */
}

.qa-status-label {
    padding: 6px;
    border: 1px solid transparent; /* No border by default */
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
    color: #666;
    font-weight: bold;
    margin-right: 6px;
}

/* Hover effect: subtle change to make it clear it's clickable */
.qa-status-label:hover:not(.disabled) {
    background-color: rgba(0, 0, 0, 0.1); /* Light darkening of the background */
    border-color: rgba(0, 0, 0, 0.2); /* Add a subtle border on hover */
}

.qa-status-label.disabled {
    color: #ccc; /* Grayed out color */
    cursor: not-allowed; /* Change the cursor to indicate it's disabled */
    opacity: 0.6; /* Dim the element to indicate it's disabled */
}

/* Adding vertical dividers between grid items */
.status-bar > div:not(:last-child) {
    border-right: 1px solid #ddd;
}

/* Status bar container green background for completed status */
.completed-status-bar {
    background-color: rgba(0, 255, 0, 0.2); /* Light green background */
}

/* Status bar container red background for rejected status */
.rejected-status-bar {
    background-color: rgba(255, 0, 0, 0.2); /* Light red background */
}

/* Style for the changed QA button */
.qa-status-button.changed-qa {
    background-color: yellow;
}

.qa-status-button.locked {
    background-color: red;
}

/* Style for the approved (completed) QA status */
.qa-approved {
    background-color: rgba(0, 255, 0, 0.2); /* Light green background */
    border-radius: 8px; /* Optional: Add rounded corners */
}

.qa-rejected {
    background-color: rgba(255, 0, 0, 0.2);
    border-radius: 8px; /* Optional: Add rounded corners */
}

.user-list-tooltip {
    position: absolute; /* Position relative to the lock button */
    bottom: 150%; /* Place the tooltip above the button */
    left: 50%;
    transform: translateX(-50%); /* Center the tooltip horizontally */
    background-color: #f9f9f9;
    border: 1px solid #ccc;
    padding: 10px;
    border-radius: 4px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    white-space: nowrap;
    z-index: 1000;
}

.user-list-tooltip::after {
    content: "";
    position: absolute;
    top: 100%; /* Arrow pointing downwards, below the tooltip */
    left: 50%;
    transform: translateX(-50%);
    border-width: 6px;
    border-style: solid;
    border-color: #f9f9f9 transparent transparent transparent; /* Tooltip arrow */
}

/* Controls Sidebar */

.toolbar {
    position: fixed;
    top: 50%; /* Center the toolbar vertically */
    right: 30px; /* Keep the toolbar pinned to the right side of the page */
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 15px;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    width: 220px;
    gap: 15px;
    background-color: #f9f9f9;
}

.toolbar-controls {
    display: flex;
    justify-content: center;
    gap: 15px; /* Adjusted space between top buttons */
}

.medium-icon-button {
    background-color: transparent;
    color: black;
    border: 1px solid gray;
    padding: 10px 12px;
    border-radius: 4px;
    font-size: 18px;
    cursor: pointer;
    transition: background-color 0.3s, color 0.3s;
}

.medium-icon-button:disabled {
    color: gray;
    cursor: not-allowed;
}

.medium-icon-button:hover:not(:disabled) {
    background-color: #f0f0f0;
}

.small-icon-button {
    background-color: transparent;
    color: black;
    border: 1px solid gray;
    padding: 6px 8px; /* Smaller button size for eye, minus, and plus */
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px; /* Smaller icon size */
    transition: background-color 0.3s, color 0.3s;
}

.small-icon-button:disabled {
    color: gray;
    cursor: not-allowed;
}

.small-icon-button:hover:not(:disabled) {
    background-color: #f0f0f0;
}

.divider {
    width: 100%;
    border: 1px solid #ddd;
    margin: 10px 0;
}

.toolbar-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
}

.label-icon-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px; /* Space between the icon and the label */
    width: 100%;
}

.eye-icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px; /* Controls the width of the eye icon column */
    height: 48px; /* Ensures consistent height for eye icons */
}

.label-and-count {
    display: flex;
    flex-direction: column;
    align-items: center; /* Centers the labels and counts vertically */
    width: 100px; /* Ensures both labels have the same width */
}

.icon-row {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 8px;
}

.section-label {
    font-size: 16px;
    font-weight: bold;
    color: #333;
    text-align: center; /* Centers the labels */
}

.image-count {
    font-size: 14px;
    color: #666;
    text-align: center; /* Centers the count directly below the label */
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.spin {
    animation: spin 1s linear infinite;
}

.total-count {
    font-size: 15px;
    color: #666;
    text-align: center;
}

/* Bulk QA Styles */
.bulk-qa-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 10px 0;
    width: 100%;
}

.bulk-qa-header {
    display: flex;
    align-items: center;
    gap: 25px; /* Further increase gap between title and button */
}

.bulk-qa-apply-button {
    min-width: 32px; /* Slightly smaller for tighter spacing */
    flex-shrink: 0; /* Prevent button from shrinking */
}

.bulk-qa-columns {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
}

.bulk-qa-column {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.bulk-qa-divider {
    width: 1px;
    background-color: #ddd;
    margin: 0 10px;
    min-height: 120px; /* Adjust based on content height */
}

.radio-group {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.radio-group-label {
    font-size: 14px;
    font-weight: bold;
    color: #333;
    margin-bottom: 2px;
}

.radio-option {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    font-size: 14px;
    padding: 2px 0;
}

.radio-option input[type="radio"] {
    margin: 0;
    cursor: pointer;
}

.radio-option input[type="radio"]:disabled {
    cursor: not-allowed;
}

.radio-option span.disabled {
    color: #ccc;
    cursor: not-allowed;
}
</style>
